import { Controller } from "@hotwired/stimulus"

// Manages component demo interactions: iframe resize, tab switching, theme toggle, code copy
export default class extends Controller {
  static targets = [
    "iframe",
    "loading",
    "previewPanel",
    "codePanel",
    "previewTab",
    "codeTab",
    "code",
    "sunIcon",
    "moonIcon",
    "copyIcon",
    "checkIcon",
    "shapeLabel"
  ]

  static values = {
    component: String,
    example: String,
    theme: { type: String, default: "light" },
    // "" (the component's own defaults), then the two shape themes the demo
    // ships. Separate from `theme`, which is the light/dark toggle.
    shapeTheme: { type: String, default: "" }
  }

  static SHAPE_THEMES = ["", "brutal", "soft"]

  connect() {
    this.boundMessageHandler = this.handleMessage.bind(this)
    window.addEventListener("message", this.boundMessageHandler)

    if (this.hasIframeTarget) {
      this.iframeTarget.addEventListener("load", () => this.hideLoading())
    }
  }

  disconnect() {
    window.removeEventListener("message", this.boundMessageHandler)
  }

  handleMessage(event) {
    const allowedOrigins = [
      "https://demo.maquina.app",
      "http://127.0.0.1:5300",
      "http://localhost:5300"
    ]

    if (!allowedOrigins.includes(event.origin)) return

    const { type, height, component, example } = event.data

    if (
      type === "maquina:resize" &&
      component === this.componentValue &&
      example === this.exampleValue
    ) {
      this.resizeIframe(height)
    }
  }

  resizeIframe(height) {
    if (this.hasIframeTarget && height > 0) {
      this.iframeTarget.style.height = `${height + 16}px`
      this.hideLoading()
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("is-hidden")
    }
  }

  // The code panel and tab only render when a demo passes `code:`, and most do
  // not — so these have to tolerate the targets being absent.
  showPreview() {
    if (!this.hasCodePanelTarget) return

    this.previewPanelTarget.classList.remove("hidden")
    this.codePanelTarget.classList.add("hidden")
    this.previewTabTarget.classList.add("is-active")
    this.codeTabTarget.classList.remove("is-active")
  }

  showCode() {
    if (!this.hasCodePanelTarget) return

    this.previewPanelTarget.classList.add("hidden")
    this.codePanelTarget.classList.remove("hidden")
    this.codeTabTarget.classList.add("is-active")
    this.previewTabTarget.classList.remove("is-active")
  }

  toggleTheme() {
    this.themeValue = this.themeValue === "light" ? "dark" : "light"
    this.reloadPreview("theme", this.themeValue)

    if (this.hasSunIconTarget && this.hasMoonIconTarget) {
      this.sunIconTarget.classList.toggle("hidden", this.themeValue === "dark")
      this.moonIconTarget.classList.toggle("hidden", this.themeValue === "light")
    }
  }

  // Cycles default -> brutal -> soft. The shape themes are pure token
  // declarations in the demo app, so this shows the component library changing
  // shape without a single component-level selector.
  toggleShape() {
    const themes = this.constructor.SHAPE_THEMES
    const next = (themes.indexOf(this.shapeThemeValue) + 1) % themes.length
    this.shapeThemeValue = themes[next]

    this.reloadPreview("shape_theme", this.shapeThemeValue)

    if (this.hasShapeLabelTarget) {
      this.shapeLabelTarget.textContent = this.shapeThemeValue || "default"
    }
  }

  // The preview app reads its theming from query params, so every toggle is an
  // iframe reload. It re-reports its height on load, which hides the spinner.
  reloadPreview(param, value) {
    if (!this.hasIframeTarget) return

    const url = new URL(this.iframeTarget.src)
    if (value) {
      url.searchParams.set(param, value)
    } else {
      url.searchParams.delete(param)
    }

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("is-hidden")
    }

    this.iframeTarget.src = url.toString()
  }

  async copyCode() {
    if (!this.hasCodeTarget) return

    try {
      await navigator.clipboard.writeText(this.codeTarget.textContent)

      if (this.hasCopyIconTarget && this.hasCheckIconTarget) {
        this.copyIconTarget.classList.add("hidden")
        this.checkIconTarget.classList.remove("hidden")

        setTimeout(() => {
          this.copyIconTarget.classList.remove("hidden")
          this.checkIconTarget.classList.add("hidden")
        }, 2000)
      }
    } catch (err) {
      console.error("Copy failed:", err)
    }
  }
}
